/**
 * Marketing → PhotoClear registration entry.
 * Validates return destinations and persists a selected credit pack/plan
 * through Google OAuth and magic-link round trips.
 */

const PLAN_KEY = 'photoclear_pending_plan';
const INTENT_KEY = 'photoclear_auth_intent';
const CTA_KEY = 'photoclear_cta_location';
const NEXT_KEY = 'photoclear_auth_next';

const TRUSTED_NEXT = new Set(['/', '/dashboard']);

const TRUSTED_PLANS = new Set([
  'credits_20',
  'credits_50_v2',
  'credits_150_v2',
  'credits_500',
  'starter_v2',
  'pro_v2',
  'studio_v2',
  'pro_v3',
  'studio_v3',
  'credits_10',
  'credits_25',
  'credits_50',
  'credits_100',
  'credits_150',
  'credits_300',
  'starter',
  'regular',
  'pro',
  'business',
  'agency',
  'enterprise',
]);

export type AuthMode = 'login' | 'signup';

export interface AuthEntry {
  mode: AuthMode;
  plan: string | null;
  next: string;
  cta: string | null;
}

export function sanitiseNext(raw: string | null | undefined): string {
  if (!raw) return '/';
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return '/';
  const path = raw.split('?')[0].split('#')[0];
  return TRUSTED_NEXT.has(path) ? path : '/';
}

export function sanitisePlan(raw: string | null | undefined): string | null {
  if (!raw) return null;
  return TRUSTED_PLANS.has(raw) ? raw : null;
}

export function readAuthEntry(search = typeof window !== 'undefined' ? window.location.search : ''): AuthEntry {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const signupFlag = params.get('signup') === '1' || params.get('mode') === 'signup';
  return {
    mode: signupFlag ? 'signup' : 'login',
    plan: sanitisePlan(params.get('plan') || params.get('pack')),
    next: sanitiseNext(params.get('next')),
    cta: params.get('cta'),
  };
}

function write(key: string, value: string | null) {
  try {
    if (value) {
      sessionStorage.setItem(key, value);
      localStorage.setItem(key, value);
    } else {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    }
  } catch {
    /* ignore */
  }
}

function read(key: string): string | null {
  try {
    return sessionStorage.getItem(key) || localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function persistAuthEntry(entry: Partial<AuthEntry>): void {
  if (entry.mode === 'login' || entry.mode === 'signup') write(INTENT_KEY, entry.mode);
  if (entry.plan) write(PLAN_KEY, entry.plan);
  if (entry.cta) write(CTA_KEY, entry.cta);
  if (entry.next) write(NEXT_KEY, sanitiseNext(entry.next));
}

/** Persist only when the URL actually carries auth/plan/cta params.
 * Avoids wiping a stored plan on `/auth/callback` or a plain `/` visit. */
export function persistAuthParamsFromUrl(search?: string): void {
  const raw = search ?? (typeof window !== 'undefined' ? window.location.search : '');
  const params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
  const hasExplicit =
    params.has('mode') ||
    params.get('signup') === '1' ||
    params.has('plan') ||
    params.has('pack') ||
    params.has('cta') ||
    params.has('next');
  if (!hasExplicit) return;
  persistAuthEntry(readAuthEntry(raw));
}

export function consumeNext(): string {
  const next = sanitiseNext(read(NEXT_KEY));
  write(NEXT_KEY, null);
  return next;
}

export function consumePendingPlan(): string | null {
  const plan = sanitisePlan(read(PLAN_KEY));
  write(PLAN_KEY, null);
  return plan;
}

export function peekPendingPlan(): string | null {
  return sanitisePlan(read(PLAN_KEY));
}

export function consumeAuthIntent(): AuthMode | null {
  const value = read(INTENT_KEY);
  write(INTENT_KEY, null);
  return value === 'signup' || value === 'login' ? value : null;
}

export function consumeCtaLocation(): string | null {
  const value = read(CTA_KEY);
  write(CTA_KEY, null);
  return value;
}

export function isSignupPath(pathname: string, search: string): boolean {
  if (pathname === '/login') {
    return readAuthEntry(search).mode === 'signup';
  }
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return params.get('signup') === '1';
}
